import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, School, Users, Target, Eye } from "lucide-react";
import WeeklyReport, { STATUS_META } from "@/components/WeeklyReport";

const SKILL_NAMES = ["Addition to 18", "Subtraction from 18", "Multiplication to 144", "Division from 144"];

export default function AdminDashboard() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [cls, setCls] = useState(null);
  const [student, setStudent] = useState(null);

  useEffect(() => {
    let alive = true;
    API.get("/admin/overview", { params: { week_offset: offset } })
      .then(({ data }) => alive && setData(data))
      .catch(() => toast.error("Failed to load school overview"));
    return () => { alive = false; };
  }, [offset]);

  const openStudent = async (id) => {
    try {
      const { data } = await API.get(`/admin/student/${id}`);
      setStudent(data);
    } catch {
      toast.error("Failed to load student");
    }
  };

  if (student) return <StudentView s={student} onBack={() => setStudent(null)} />;

  if (cls) {
    return (
      <div className="min-h-screen p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-class-view">
        <button onClick={() => setCls(null)} className="flex items-center gap-2 text-white/40 hover:text-white mb-6 font-body text-sm transition-colors duration-200" data-testid="admin-class-back">
          <ArrowLeft className="w-4 h-4" /> Back to School Overview
        </button>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">{cls.name}</h1>
        <p className="font-body text-white/40 mb-6">{cls.teacher} · {cls.student_count} students · click a student for details</p>
        <WeeklyReport endpoint="/admin/weekly-report" classId={cls.id} onSelect={openStudent} initialOffset={offset} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs text-[#FFE600] tracking-[0.3em] uppercase mb-2 flex items-center gap-2">
            <Eye className="w-3.5 h-3.5" /> View only
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-white tracking-tighter">School Overview</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOffset((o) => o + 1)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors duration-200" data-testid="admin-week-prev">
            <ChevronLeft className="w-4 h-4 text-white/60" />
          </button>
          <span className="font-mono text-sm text-white/70 w-40 text-center" data-testid="admin-week-label">
            {offset === 0 ? "This week" : offset === 1 ? "Last week" : `${offset} weeks ago`}
            {data && <span className="block text-xs text-white/30">from {data.week_start}</span>}
          </span>
          <button onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors duration-200" data-testid="admin-week-next">
            <ChevronRight className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </div>

      {!data ? (
        <p className="font-mono text-cyber animate-pulse">LOADING SCHOOL DATA...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <Tile icon={Users} label="Students" value={data.total_students} testid="admin-total-students" />
            <Tile icon={Target} label={`Avg facts (goal ${data.goal})`} value={data.avg_facts_mastered} testid="admin-avg-facts" />
            {Object.entries(STATUS_META).map(([k, m]) => (
              <Tile key={k} label={m.label} value={data.summary[k] || 0} color={m.text} dot={m.dot} testid={`admin-summary-${k}`} />
            ))}
          </div>
          {data.unassigned_students > 0 && (
            <p className="font-body text-xs text-white/40 mb-6" data-testid="admin-unassigned">{data.unassigned_students} student(s) haven't joined a class yet and aren't included.</p>
          )}
          {data.teachers.length === 0 && <p className="font-body text-white/40" data-testid="admin-no-classes">No classes have been created yet.</p>}
          <div className="space-y-8">
            {data.teachers.map((t) => (
              <section key={t.id} data-testid={`admin-teacher-${t.id}`}>
                <h2 className="font-heading text-lg font-bold text-white mb-1">{t.name}</h2>
                <p className="font-body text-xs text-white/30 mb-4">{t.email}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {t.classes.map((c) => (
                    <ClassCard key={c.id} c={c} onOpen={() => setCls({ ...c, teacher: t.name })} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, color = "text-white", dot, testid }) {
  return (
    <div className="glass-surface rounded-xl p-5" data-testid={testid}>
      <p className="font-mono text-xs text-white/40 uppercase tracking-wider mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}{dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}{label}
      </p>
      <p className={`font-heading text-3xl font-black ${color}`}>{value}</p>
    </div>
  );
}

function ClassCard({ c, onOpen }) {
  const total = Math.max(c.student_count, 1);
  return (
    <button onClick={onOpen} data-testid={`admin-class-${c.id}`}
      className="text-left glass-surface rounded-xl p-5 border border-white/5 hover:border-cyber/40 hover:-translate-y-1 transition-[transform,border-color] duration-200">
      <div className="flex items-center gap-2 mb-1">
        <School className="w-4 h-4 text-cyber" />
        <p className="font-heading text-base font-bold text-white">{c.name}</p>
      </div>
      <p className="font-body text-xs text-white/40 mb-4">{c.student_count} students · {c.practiced} practiced this week</p>
      <div className="flex h-2 rounded-full overflow-hidden bg-white/5 mb-3">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className={m.dot} style={{ width: `${((c.summary[k] || 0) / total) * 100}%` }} />
        ))}
      </div>
      <div className="flex justify-between font-mono text-xs">
        <span className="text-white/50">Avg facts: <span className="text-white">{c.avg_facts_mastered}</span></span>
        <span className="text-white/50">Accuracy: <span className="text-white">{c.avg_accuracy}%</span></span>
      </div>
    </button>
  );
}

function StudentView({ s, onBack }) {
  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-5xl mx-auto" data-testid="admin-student-view">
      <button onClick={onBack} className="flex items-center gap-2 text-white/40 hover:text-white mb-6 font-body text-sm transition-colors duration-200" data-testid="admin-student-back">
        <ArrowLeft className="w-4 h-4" /> Back to class
      </button>
      <h1 className="font-heading text-3xl font-bold text-white tracking-tight" data-testid="admin-student-name">{s.name}</h1>
      <p className="font-body text-white/40 mb-6">{s.email}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Tile label="Accuracy" value={`${s.accuracy}%`} />
        <Tile label="Skill Band" value={`${(s.current_skill_band || 0) + 1}`} />
        <Tile label="Total Attempts" value={s.total_attempts} />
        <Tile label="Ship Level" value={(s.ship_level || 0) + 1} />
      </div>
      <p className="font-body text-sm text-white/50 mb-3">Current band: {SKILL_NAMES[s.current_skill_band || 0]}</p>
      <div className="glass-surface rounded-xl p-5">
        <h2 className="font-heading text-base font-bold text-white mb-3">Recent Problems</h2>
        {s.recent_attempts?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {s.recent_attempts.slice(0, 30).map((a) => (
              <div key={a._id} className={`flex justify-between px-3 py-2 rounded-lg font-mono text-xs ${a.is_correct ? "bg-[#39FF14]/5 text-[#39FF14]" : "bg-magenta/5 text-magenta"}`}>
                <span>{a.problem_text}</span>
                <span>{a.is_correct ? "✓" : `✗ ${a.student_answer ?? ""}`} {a.time_taken ? `${Number(a.time_taken).toFixed(1)}s` : ""}</span>
              </div>
            ))}
          </div>
        ) : <p className="font-body text-sm text-white/30">No practice yet</p>}
      </div>
    </div>
  );
}
